import { Formik, Form } from 'formik';
import * as Yup from 'yup';
import { SelectField, TextField } from '../../common/InputField';
import Button from '../../common/Button';
import { useAppContext } from '../../../utils/context/app-context';
import { StatusValues, ToastType } from '../../../types/common.types';
import { useParams } from 'react-router-dom';
import { useEffect, useState } from 'react';
import {
  AutoModeComplexity,
  AutoModeCost,
  AutoModeSpeed,
  TAIChatModel,
} from '../../../types/chat-ai-models';
import AiChatModelService from '../../../services/ai-chat-models.service';
import Switch from '../../common/Switch';

const CreateAIModelSchema = Yup.object().shape({
  platform: Yup.string()
    .required('Platform is required.')
    .min(1)
    .max(100)
    .trim(),
  aiModelName: Yup.string()
    .required('Ai Model Name is required.')
    .min(1)
    .max(100)
    .trim(),
  publicName: Yup.string()
    .required('Public Name is required.')
    .min(1)
    .max(100)
    .trim(),
  description: Yup.string()
    .required('Description is required.')
    .min(1)
    .max(100)
    .trim(),
  icon: Yup.string().required('Icon is required.').min(1).max(100).trim(),
  status: Yup.string().required('Status is required.'),
  autoModeStrengths: Yup.array()
    .of(
      Yup.string().oneOf([
        'code',
        'reasoning',
        'general',
        'math',
        'creativity',
        'concise',
      ])
    )
    .min(1, 'At least one strength is required')
    .required('Auto-mode strengths are required'),
  autoModeSpeed: Yup.string()
    .oneOf(['fast', 'medium', 'slow'])
    .required('Auto-mode speed is required'),
  autoModeComplexity: Yup.string()
    .oneOf(['low', 'medium', 'high'])
    .required('Auto-mode complexity is required'),
  autoModeCost: Yup.string()
    .oneOf(['low', 'medium', 'high'])
    .required('Auto-mode cost is required'),
  autoModeEnabled: Yup.boolean().required(),
  usageMultiplier: Yup.number()
    .min(0)
    .required('Usage Multiplier is required.')
    .default(1.0),
});

const CreateAIModel = () => {
  const { setToastInfo } = useAppContext();
  const { id } = useParams();
  const [initialValues, setInitialValues] = useState<TAIChatModel>({
    platform: '',
    aiModelName: '',
    publicName: '',
    description: '',
    icon: '',
    imageGeneration: false,
    realTimeData: false,
    fileAnalysis: false,
    reasoning: false,
    enabledForFree: false,
    autoModeStrengths: ['general'],
    autoModeSpeed: AutoModeSpeed.MEDIUM,
    autoModeComplexity: AutoModeComplexity.MEDIUM,
    autoModeCost: AutoModeCost.MEDIUM,
    autoModeEnabled: true,
    usageMultiplier: 1.0,
    status: StatusValues.ACTIVE,
  });

  const getAiModelById = async () => {
    try {
      const response = await AiChatModelService.getAiModelById(id ?? '');
      if (response.data.data && response.data.data.model) {
        const aiChatModel = response.data.data.model;
        setInitialValues({
          platform: aiChatModel.platform,
          aiModelName: aiChatModel.aiModelName,
          publicName: aiChatModel.publicName,
          description: aiChatModel.description,
          enabledForFree: aiChatModel.enabledForFree,
          imageGeneration: aiChatModel.imageGeneration,
          fileAnalysis: aiChatModel.fileAnalysis,
          reasoning: aiChatModel.reasoning || false,
          realTimeData: aiChatModel.realTimeData ?? false,
          icon: aiChatModel.icon,
          autoModeStrengths: aiChatModel.autoModeStrengths || ['general'],
          autoModeSpeed: aiChatModel.autoModeSpeed || 'medium',
          autoModeComplexity: aiChatModel.autoModeComplexity || 'medium',
          autoModeCost: aiChatModel.autoModeCost || 'medium',
          autoModeEnabled: aiChatModel.autoModeEnabled ?? true,
          usageMultiplier: aiChatModel.usageMultiplier || 1.0,
          status: aiChatModel.status,
        });
      } else {
        setToastInfo({
          isOpen: true,
          type: ToastType.ERROR,
          message: response.data.message,
        });
      }
    } catch (err) {
      setToastInfo({
        isOpen: true,
        type: ToastType.ERROR,
        message: 'Error in fetching Ai Model.',
      });
    }
  };

  const savePlan = async (values: TAIChatModel) => {
    try {
      const response = await AiChatModelService.createAiChatModel(values);
      if (response.data.data && response.data.data.model) {
        setToastInfo({
          isOpen: true,
          type: ToastType.SUCCESS,
          message: response.data.message,
        });
        setInitialValues({
          platform: '',
          aiModelName: '',
          publicName: '',
          description: '',
          enabledForFree: false,
          imageGeneration: false,
          fileAnalysis: false,
          realTimeData: false,
          icon: '',
          reasoning: false,
          autoModeStrengths: ['general'],
          autoModeSpeed: AutoModeSpeed.MEDIUM,
          autoModeComplexity: AutoModeComplexity.MEDIUM,
          autoModeCost: AutoModeCost.MEDIUM,
          autoModeEnabled: true,
          usageMultiplier: 1.0,
          status: StatusValues.ACTIVE,
        });
      } else {
        setToastInfo({
          isOpen: true,
          type: ToastType.ERROR,
          message: response.data.message,
        });
      }
    } catch (err) {
      console.log('Error: ', err);
      setToastInfo({
        isOpen: true,
        type: ToastType.ERROR,
        message: 'Error in creating ai model',
      });
    }
  };

  const updatePlan = async (values: TAIChatModel) => {
    try {
      const response = await AiChatModelService.updateAiChatModel({
        ...values,
        _id: id,
      });
      if (response.data.data && response.data.data.model) {
        setToastInfo({
          isOpen: true,
          type: ToastType.SUCCESS,
          message: response.data.message,
        });
        const aiChatModel = response.data.data.model;
        setInitialValues({
          platform: aiChatModel.platform,
          aiModelName: aiChatModel.aiModelName,
          publicName: aiChatModel.publicName,
          description: aiChatModel.description,
          enabledForFree: aiChatModel.enabledForFree,
          imageGeneration: aiChatModel.imageGeneration,
          fileAnalysis: aiChatModel.fileAnalysis,
          realTimeData: aiChatModel.realTimeData ?? false,
          reasoning: aiChatModel.reasoning || false,
          icon: aiChatModel.icon,
          autoModeStrengths: aiChatModel.autoModeStrengths || ['general'],
          autoModeSpeed: aiChatModel.autoModeSpeed || 'medium',
          autoModeComplexity: aiChatModel.autoModeComplexity || 'medium',
          autoModeCost: aiChatModel.autoModeCost || 'medium',
          autoModeEnabled: aiChatModel.autoModeEnabled ?? true,
          usageMultiplier: aiChatModel.usageMultiplier || 1.0,
          status: aiChatModel.status,
        });
      } else {
        setToastInfo({
          isOpen: true,
          type: ToastType.ERROR,
          message: response.data.message,
        });
      }
    } catch (err) {
      console.log('Error: ', err);
      setToastInfo({
        isOpen: true,
        type: ToastType.ERROR,
        message: 'Error in updating plan',
      });
    }
  };

  useEffect(() => {
    if (id) {
      getAiModelById();
    }
  }, [id]);

  const statusItems = [
    {
      title: 'Active',
      value: 'active',
    },
    {
      title: 'Inactive',
      value: 'inactive',
    },
    {
      title: 'Deleted',
      value: 'deleted',
    },
  ];

  const strengthItems = [
    { title: 'Code', value: 'code' },
    { title: 'Reasoning', value: 'reasoning' },
    { title: 'General', value: 'general' },
    { title: 'Math', value: 'math' },
    { title: 'Creativity', value: 'creativity' },
    { title: 'Concise', value: 'concise' },
  ];

  const speedItems = [
    { title: 'Fast', value: AutoModeSpeed.FAST },
    { title: 'Medium', value: AutoModeSpeed.MEDIUM },
    { title: 'Slow', value: AutoModeSpeed.SLOW },
  ];

  const complexityItems = [
    { title: 'Low', value: AutoModeComplexity.LOW },
    { title: 'Medium', value: AutoModeComplexity.MEDIUM },
    { title: 'High', value: AutoModeComplexity.HIGH },
  ];

  const costItems = [
    { title: 'Low', value: AutoModeCost.LOW },
    { title: 'Medium', value: AutoModeCost.MEDIUM },
    { title: 'High', value: AutoModeCost.HIGH },
  ];

  return (
    <div className="w-full pb-10">
      <div className="border border-[rgba(0,0,0,.15)] rounded-3xl p-10">
        <Formik
          initialValues={initialValues}
          validationSchema={CreateAIModelSchema}
          validateOnChange={false}
          validateOnBlur={true}
          enableReinitialize={true}
          onSubmit={(values, { resetForm }) => {
            if (id) {
              updatePlan(values);
            } else {
              savePlan(values);
            }
            resetForm();
          }}
        >
          {({
            errors,
            touched,
            handleChange,
            handleSubmit,
            handleBlur,
            isValid,
            values,
            setFieldValue,
          }) => {
            return (
              <Form>
                <div className="grid grid-cols-3 gap-4">
                  <div className="mb-5">
                    <TextField
                      name="platform"
                      label="Platform"
                      placeholder=""
                      onChange={handleChange}
                      onBlur={handleBlur}
                      error={errors.platform}
                      required={true}
                      isTouched={touched.platform}
                      value={values.platform}
                    />
                  </div>

                  <div className="mb-5">
                    <TextField
                      name="aiModelName"
                      label="Ai Model Name"
                      placeholder=""
                      onChange={handleChange}
                      onBlur={handleBlur}
                      error={errors.aiModelName}
                      required={true}
                      isTouched={touched.aiModelName}
                      value={values.aiModelName}
                    />
                  </div>

                  <div className="mb-5">
                    <TextField
                      name="publicName"
                      label="Public Name"
                      placeholder=""
                      onChange={handleChange}
                      onBlur={handleBlur}
                      error={errors.publicName}
                      required={true}
                      isTouched={touched.publicName}
                      value={values.publicName}
                    />
                  </div>
                </div>

                <div className="grid grid-cols-3 gap-4">
                  <div className="mb-5 col-span-3">
                    <TextField
                      name="description"
                      label="Description"
                      placeholder=""
                      onChange={handleChange}
                      onBlur={handleBlur}
                      error={errors.description}
                      required={true}
                      isTouched={touched.description}
                      value={values.description}
                    />
                  </div>
                </div>

                <div className="grid grid-cols-5 gap-4">
                  <div className="mb-5">
                    <Switch
                      label="Enabled For Free"
                      handleClick={() => {
                        setFieldValue('enabledForFree', !values.enabledForFree);
                      }}
                      active={values.enabledForFree}
                    />
                  </div>
                  <div className="mb-5">
                    <Switch
                      label="Image Generation"
                      handleClick={() => {
                        setFieldValue(
                          'imageGeneration',
                          !values.imageGeneration
                        );
                      }}
                      active={values.imageGeneration}
                    />
                  </div>
                  <div className="mb-5">
                    <Switch
                      label="File Analysis"
                      handleClick={() => {
                        setFieldValue('fileAnalysis', !values.fileAnalysis);
                      }}
                      active={values.fileAnalysis}
                    />
                  </div>
                  <div className="mb-5">
                    <Switch
                      label="Real Time Data"
                      handleClick={() => {
                        setFieldValue('realTimeData', !values.realTimeData);
                      }}
                      active={values.realTimeData}
                    />
                  </div>
                  <div className="mb-5">
                    <Switch
                      label="Reasoning"
                      handleClick={() => {
                        setFieldValue('reasoning', !values.reasoning);
                      }}
                      active={values.reasoning}
                    />
                  </div>
                </div>

                {/* Auto-Mode Configuration Section */}
                <div className="mb-8">
                  <h3 className="text-lg font-semibold mb-4 text-gray-700">
                    Auto-Mode Configuration
                  </h3>

                  <div className="grid grid-cols-2 gap-4 mb-4">
                    <div className="mb-5">
                      <Switch
                        label="Auto-Mode Enabled"
                        handleClick={() => {
                          setFieldValue(
                            'autoModeEnabled',
                            !values.autoModeEnabled
                          );
                        }}
                        active={values.autoModeEnabled}
                      />
                    </div>
                  </div>

                  <div className="grid grid-cols-3 gap-4">
                    <div className="mb-5">
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Strengths <span className="text-red-500">*</span>
                      </label>
                      <div className="space-y-2">
                        {strengthItems.map((item) => (
                          <label key={item.value} className="flex items-center">
                            <input
                              type="checkbox"
                              checked={values.autoModeStrengths.includes(
                                item.value
                              )}
                              onChange={(e) => {
                                const currentStrengths =
                                  values.autoModeStrengths;
                                if (e.target.checked) {
                                  setFieldValue('autoModeStrengths', [
                                    ...currentStrengths,
                                    item.value,
                                  ]);
                                } else {
                                  setFieldValue(
                                    'autoModeStrengths',
                                    currentStrengths.filter(
                                      (s) => s !== item.value
                                    )
                                  );
                                }
                              }}
                              className="mr-2"
                            />
                            <span className="text-sm">{item.title}</span>
                          </label>
                        ))}
                      </div>
                      {errors.autoModeStrengths &&
                        touched.autoModeStrengths && (
                          <div className="text-red-500 text-sm mt-1">
                            {errors.autoModeStrengths}
                          </div>
                        )}
                    </div>

                    <div className="mb-5">
                      <SelectField
                        name="autoModeSpeed"
                        label="Speed"
                        onChange={handleChange}
                        onBlur={handleBlur}
                        error={errors.autoModeSpeed}
                        isTouched={touched.autoModeSpeed}
                        value={values.autoModeSpeed}
                        items={speedItems}
                        required={true}
                      />
                    </div>

                    <div className="mb-5">
                      <SelectField
                        name="autoModeComplexity"
                        label="Complexity"
                        onChange={handleChange}
                        onBlur={handleBlur}
                        error={errors.autoModeComplexity}
                        isTouched={touched.autoModeComplexity}
                        value={values.autoModeComplexity}
                        items={complexityItems}
                        required={true}
                      />
                    </div>
                  </div>

                  <div className="grid grid-cols-3 gap-4">
                    <div className="mb-5">
                      <SelectField
                        name="autoModeCost"
                        label="Cost"
                        onChange={handleChange}
                        onBlur={handleBlur}
                        error={errors.autoModeCost}
                        isTouched={touched.autoModeCost}
                        value={values.autoModeCost}
                        items={costItems}
                        required={true}
                      />
                    </div>
                    <div className="mb-5">
                      <TextField
                        name="usageMultiplier"
                        label="Usage Multiplier"
                        type="number"
                        placeholder=""
                        onChange={handleChange}
                        onBlur={handleBlur}
                        error={errors.usageMultiplier}
                        required={true}
                        isTouched={touched.usageMultiplier}
                        value={values.usageMultiplier}
                      />
                    </div>
                  </div>
                </div>

                <div className="grid grid-cols-3 gap-4">
                  <div className="mb-5">
                    <TextField
                      name="icon"
                      label="Icon"
                      placeholder=""
                      onChange={handleChange}
                      onBlur={handleBlur}
                      error={errors.icon}
                      required={true}
                      isTouched={touched.icon}
                      value={values.icon}
                    />
                  </div>
                  <div className="mb-5">
                    <SelectField
                      name="status"
                      label="Status"
                      onChange={handleChange}
                      onBlur={handleBlur}
                      error={errors.status}
                      isTouched={touched.status}
                      value={values.status}
                      items={statusItems}
                    />
                  </div>
                </div>

                <div className="text-center">
                  <Button
                    type="button"
                    disabled={!touched || !isValid}
                    onClick={() => handleSubmit()}
                  >
                    {id ? 'Update' : 'Submit'}
                  </Button>
                </div>
              </Form>
            );
          }}
        </Formik>
      </div>
    </div>
  );
};

export default CreateAIModel;
